/**
 * Single Claim Entry Page
 *
 * Allows entry of individual claims for the current organization.
 * Uses organization context from session - claims can only be created
 * for the currently logged-in organization.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  FileText,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
  Building2
} from 'lucide-react';

import type { ClaimCreate, ClaimLineItemCreate, Payer } from '@/services/api/claims';
import { todayLocalISO, formatDateOnly } from '@/utils/dateTime';

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  member_id: string;
}

interface Program {
  id: number;
  name: string;
}

interface BillingActivity {
  id: number;
  activity_name: string;
  activity_code?: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  place_of_service?: string;
  rate_per_unit: number;
  billing_unit: string;
}

interface ActivityDiagnosisCode {
  id: number;
  billing_activity_id: number;
  icd10_code_id: number;
  icd10_code: string;
  icd10_description: string;
  icd10_short_description?: string;
  is_default: boolean;
  display_order: number;
}

const PLACE_OF_SERVICE_CODES = [
  { value: '02', label: '02 - Telehealth (Other than Patient\'s Home)' },
  { value: '03', label: '03 - School' },
  { value: '04', label: '04 - Homeless Shelter' },
  { value: '09', label: '09 - Prison/Correctional Facility' },
  { value: '10', label: '10 - Telehealth (Patient\'s Home)' },
  { value: '11', label: '11 - Office' },
  { value: '12', label: '12 - Home' },
  { value: '13', label: '13 - Assisted Living Facility' },
  { value: '14', label: '14 - Group Home' },
  { value: '15', label: '15 - Mobile Unit' },
  { value: '16', label: '16 - Temporary Lodging' },
  { value: '17', label: '17 - Walk-in Retail Health Clinic' },
  { value: '18', label: '18 - Place of Employment/Worksite' },
  { value: '19', label: '19 - Off Campus-Outpatient Hospital' },
  { value: '20', label: '20 - Urgent Care Facility' },
  { value: '21', label: '21 - Inpatient Hospital' },
  { value: '22', label: '22 - On Campus-Outpatient Hospital' },
  { value: '23', label: '23 - Emergency Room - Hospital' },
  { value: '24', label: '24 - Ambulatory Surgical Center' },
  { value: '25', label: '25 - Birthing Center' },
  { value: '26', label: '26 - Military Treatment Facility' },
  { value: '31', label: '31 - Skilled Nursing Facility' },
  { value: '32', label: '32 - Nursing Facility' },
  { value: '33', label: '33 - Custodial Care Facility' },
  { value: '34', label: '34 - Hospice' },
  { value: '49', label: '49 - Independent Clinic' },
  { value: '50', label: '50 - Federally Qualified Health Center' },
  { value: '51', label: '51 - Inpatient Psychiatric Facility' },
  { value: '52', label: '52 - Psychiatric Facility-Partial Hospitalization' },
  { value: '53', label: '53 - Community Mental Health Center' },
  { value: '54', label: '54 - Intermediate Care Facility' },
  { value: '55', label: '55 - Residential Substance Abuse Treatment' },
  { value: '56', label: '56 - Psychiatric Residential Treatment Center' },
  { value: '57', label: '57 - Non-residential Substance Abuse Treatment' },
  { value: '60', label: '60 - Mass Immunization Center' },
  { value: '62', label: '62 - Comprehensive Outpatient Rehab' },
  { value: '65', label: '65 - End-Stage Renal Disease Treatment' },
  { value: '71', label: '71 - Public Health Clinic' },
  { value: '72', label: '72 - Rural Health Clinic' },
  { value: '81', label: '81 - Independent Laboratory' },
  { value: '99', label: '99 - Other Place of Service' }
];

interface NewClaimPageProps {
  onNavigate?: (tab: string) => void;
}

export default function NewClaimPage({ onNavigate }: NewClaimPageProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Organization from context
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');

  // Reference data
  const [payers, setPayers] = useState<Payer[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [billingActivities, setBillingActivities] = useState<BillingActivity[]>([]);
  const [programPlaceOfService, setProgramPlaceOfService] = useState<Array<{code: string; label: string; is_default: boolean}>>([]);
  const [activityDiagnosisCodes, setActivityDiagnosisCodes] = useState<Record<number, ActivityDiagnosisCode[]>>({});
  const [lineItemDiagnosisCodes, setLineItemDiagnosisCodes] = useState<ActivityDiagnosisCode[]>([]);

  // Form Data
  const [claimForm, setClaimForm] = useState<ClaimCreate>({
    organization_id: 0,
    member_id: 0,
    payer_id: 0,
    billing_provider_npi: '',
    service_date_from: '',
    service_date_to: '',
    place_of_service: '12',
    line_items: []
  });

  // Line item form
  const [lineItemForm, setLineItemForm] = useState<ClaimLineItemCreate>({
    line_number: 1,
    service_date: '',
    units: 1,
    charge_amount: 0
  });

  // Fetch organization details including name
  const fetchOrganization = async (cboId: string) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/cbos/${cboId}`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizationName(data.name || '');
        setClaimForm(prev => ({
          ...prev,
          billing_provider_npi: data.npi || prev.billing_provider_npi
        }));
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
    }
  };

  // Load initial data and set organization from localStorage
  useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    const cboName = localStorage.getItem('active_cbo_name');
    if (cboId) {
      const orgId = parseInt(cboId);
      setOrganizationId(orgId);
      setClaimForm(prev => ({
        ...prev,
        organization_id: orgId
      }));
      // If name is in localStorage, use it; otherwise fetch from API
      if (cboName) {
        setOrganizationName(cboName);
        fetchOrganizationNpi(orgId);
      } else {
        fetchOrganization(cboId);
      }
    }

    Promise.all([
      fetchPayers(),
      fetchMembers(),
      fetchPrograms(),
      fetchBillingActivities()
    ]);
  }, []);

  // Fetch program POS codes when program changes
  useEffect(() => {
    if (claimForm.program_id) {
      fetchProgramPlaceOfService(claimForm.program_id);
    } else {
      setProgramPlaceOfService([]);
    }
  }, [claimForm.program_id]);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  // Fetch functions
  const fetchPayers = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/claims/payers'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPayers(data);
      }
    } catch (err) {
      console.error('Error fetching payers:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/members?page_size=1000'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const membersList = data.members || data || [];
        const mappedMembers = membersList.map((m: any) => ({
          ...m,
          member_id: m.member_number || m.member_id || m.id
        }));
        setMembers(mappedMembers);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/programs'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const fetchBillingActivities = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/billing-activities'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBillingActivities(data);
      }
    } catch (err) {
      console.error('Error fetching billing activities:', err);
    }
  };

  const fetchOrganizationNpi = async (orgId: number) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/cbos/${orgId}`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClaimForm(prev => ({
          ...prev,
          billing_provider_npi: data.npi || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching organization NPI:', err);
    }
  };

  const fetchProgramPlaceOfService = async (programId: number) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/programs/${programId}/place-of-service`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const posCodes = data
          .filter((pos: any) => pos.is_active)
          .sort((a: any, b: any) => a.display_order - b.display_order)
          .map((pos: any) => ({
            code: pos.place_of_service_code,
            label: `${pos.place_of_service_code} - ${pos.place_of_service_label}`,
            is_default: pos.is_default
          }));
        setProgramPlaceOfService(posCodes);
        const defaultPos = posCodes.find((pos: any) => pos.is_default);
        if (defaultPos && !claimForm.place_of_service) {
          setClaimForm(prev => ({ ...prev, place_of_service: defaultPos.code }));
        }
      } else {
        setProgramPlaceOfService([]);
      }
    } catch (err) {
      console.error('Error fetching program place of service:', err);
      setProgramPlaceOfService([]);
    }
  };

  const availablePlaceOfServiceCodes = programPlaceOfService.length > 0
    ? programPlaceOfService.map(pos => ({ value: pos.code, label: pos.label }))
    : PLACE_OF_SERVICE_CODES;

  const fetchActivityDiagnosisCodes = async (activityId: number): Promise<ActivityDiagnosisCode[]> => {
    if (activityDiagnosisCodes[activityId]) {
      return activityDiagnosisCodes[activityId];
    }
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/billing-activities/${activityId}/diagnosis-codes`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActivityDiagnosisCodes(prev => ({
          ...prev,
          [activityId]: data
        }));
        return data;
      }
    } catch (err) {
      console.error('Error fetching activity diagnosis codes:', err);
    }
    return [];
  };

  // CRUD operations
  const handleCreateClaim = async () => {
    if (!claimForm.organization_id || !claimForm.member_id || !claimForm.payer_id ||
        !claimForm.service_date_from || claimForm.line_items.length === 0) {
      setError('Please fill in all required fields and add at least one line item');
      return;
    }

    const today = todayLocalISO();
    if (claimForm.service_date_from > today || claimForm.service_date_to > today) {
      setError('Service dates cannot be in the future');
      return;
    }
    const futureDateItems = claimForm.line_items.filter(item =>
      item.service_date && item.service_date > today
    );
    if (futureDateItems.length > 0) {
      setError(`${futureDateItems.length} line item(s) have service dates in the future. Please correct before submitting.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getWeaveinApiUrl('/api/claims'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(claimForm)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(`Claim ${data.claim_number} created successfully`);
        setTimeout(() => {
          onNavigate?.('billing-claims');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create claim');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create claim');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    if (!lineItemForm.service_date || !lineItemForm.charge_amount) {
      setError('Please fill in service date and charge amount');
      return;
    }

    const newLineItem: ClaimLineItemCreate = {
      ...lineItemForm,
      line_number: claimForm.line_items.length + 1
    };

    setClaimForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, newLineItem]
    }));

    setLineItemForm({
      line_number: claimForm.line_items.length + 2,
      service_date: claimForm.service_date_from,
      units: 1,
      charge_amount: 0
    });
  };

  const removeLineItem = (index: number) => {
    setClaimForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index).map((item, i) => ({
        ...item,
        line_number: i + 1
      }))
    }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => onNavigate?.('billing-claims')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Claims
          </Button>
        </div>
        <div className="flex items-center gap-3">
          {!tabbed && <FileText className="h-8 w-8 text-blue-600" />}
          <div>
            {!tabbed && <h1 className="font-display text-lg font-semibold tracking-[-0.015em] text-ink">Create New Claim</h1>}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building2 className="h-4 w-4" />
              <span>{organizationName || 'Current Organization'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p className="flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Success Display */}
      {successMessage && (
        <Card className="p-4 mb-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="h-5 w-5" />
            <p>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Claim Form */}
      <Card className="p-6">
        <div className="space-y-6">
          {/* Claim Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="payer_id">Payer *</Label>
              <select
                id="payer_id"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={claimForm.payer_id}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  payer_id: parseInt(e.target.value)
                }))}
                required
              >
                <option value="">Select payer...</option>
                {payers.map(payer => (
                  <option key={payer.id} value={payer.id}>
                    {payer.payer_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="member_id">Member *</Label>
              <select
                id="member_id"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={claimForm.member_id}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  member_id: parseInt(e.target.value)
                }))}
                required
              >
                <option value="">Select member...</option>
                {members.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.first_name} {member.last_name} ({member.member_id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="program_id">Program (Optional)</Label>
              <select
                id="program_id"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={claimForm.program_id || ''}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  program_id: e.target.value ? parseInt(e.target.value) : undefined
                }))}
              >
                <option value="">None</option>
                {programs.map(program => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="service_date_from">Service Date From *</Label>
              <Input
                id="service_date_from"
                type="date"
                value={claimForm.service_date_from}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  service_date_from: e.target.value,
                  service_date_to: e.target.value
                }))}
                max={todayLocalISO()}
                className={`focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${claimForm.service_date_from && claimForm.service_date_from > todayLocalISO() ? 'border-red-500 bg-red-50' : ''}`}
                title="Service date cannot be in the future"
                required
              />
            </div>

            <div>
              <Label htmlFor="service_date_to">Service Date To *</Label>
              <Input
                id="service_date_to"
                type="date"
                value={claimForm.service_date_to}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  service_date_to: e.target.value
                }))}
                max={todayLocalISO()}
                className={`focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${claimForm.service_date_to && claimForm.service_date_to > todayLocalISO() ? 'border-red-500 bg-red-50' : ''}`}
                title="Service date cannot be in the future"
                required
              />
            </div>

            <div>
              <Label htmlFor="place_of_service">
                Place of Service
                {programPlaceOfService.length > 0 && (
                  <span className="ml-2 text-xs text-blue-600">(Program-specific)</span>
                )}
              </Label>
              <select
                id="place_of_service"
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${programPlaceOfService.length > 0 ? 'bg-blue-50' : ''}`}
                value={claimForm.place_of_service}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  place_of_service: e.target.value
                }))}
              >
                {availablePlaceOfServiceCodes.map(pos => (
                  <option key={pos.value} value={pos.value}>
                    {pos.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="billing_provider_npi">Billing Provider NPI *</Label>
              <Input
                id="billing_provider_npi"
                value={claimForm.billing_provider_npi}
                onChange={e => setClaimForm(prev => ({
                  ...prev,
                  billing_provider_npi: e.target.value
                }))}
                placeholder="10-digit NPI"
                maxLength={10}
                className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="diagnosis_code_1">
                Primary Diagnosis Code (Z-code supported)
                {lineItemDiagnosisCodes.length > 0 && (
                  <span className="ml-2 text-xs text-green-600">(Activity-linked)</span>
                )}
              </Label>
              {lineItemDiagnosisCodes.length > 0 ? (
                <select
                  id="diagnosis_code_1"
                  className="w-full p-2 border rounded-md bg-green-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={claimForm.diagnosis_code_1 || ''}
                  onChange={e => setClaimForm(prev => ({
                    ...prev,
                    diagnosis_code_1: e.target.value
                  }))}
                >
                  <option value="">Select diagnosis code...</option>
                  {lineItemDiagnosisCodes.map(diag => (
                    <option key={diag.icd10_code_id} value={diag.icd10_code}>
                      {diag.icd10_code} - {diag.icd10_short_description || diag.icd10_description}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id="diagnosis_code_1"
                  value={claimForm.diagnosis_code_1 || ''}
                  onChange={e => setClaimForm(prev => ({
                    ...prev,
                    diagnosis_code_1: e.target.value.toUpperCase()
                  }))}
                  placeholder="e.g., Z59.0 (or select billing activity for linked codes)"
                  className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            </div>
          </div>

          {/* Line Items Section */}
          <div className="border-t pt-6">
            <h3 className="font-semibold mb-4">Line Items</h3>

            {/* Existing Line Items */}
            {claimForm.line_items.length > 0 && (
              <div className="mb-4 overflow-x-auto">
                <table className="w-full text-sm border rounded-md overflow-hidden min-w-[800px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Line</th>
                      <th className="text-left p-2">CPT/HCPCS</th>
                      <th className="text-left p-2">Rev</th>
                      <th className="text-left p-2">Mod</th>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">POS</th>
                      <th className="text-right p-2">Units</th>
                      <th className="text-right p-2">Charge</th>
                      <th className="text-right p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimForm.line_items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2">{item.line_number}</td>
                        <td className="p-2">{item.cpt_code || item.hcpcs_code || 'N/A'}</td>
                        <td className="p-2">{item.revenue_code || '-'}</td>
                        <td className="p-2">
                          {[item.modifier_1, item.modifier_2].filter(Boolean).join(', ') || '-'}
                        </td>
                        <td className="p-2">{formatDateOnly(item.service_date)}</td>
                        <td className="p-2">{item.place_of_service || claimForm.place_of_service}</td>
                        <td className="text-right p-2">{item.units}</td>
                        <td className="text-right p-2">${Number(item.charge_amount || 0).toFixed(2)}</td>
                        <td className="text-right p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-right font-semibold">
                  Total: ${claimForm.line_items.reduce((sum, item) => sum + Number(item.charge_amount || 0) * item.units, 0).toFixed(2)}
                </div>
              </div>
            )}

            {/* Add Line Item Form */}
            <Card className="p-4 bg-gray-50">
              <h4 className="font-medium mb-3">Add Line Item</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Billing Activity Selector */}
                <div className="md:col-span-2">
                  <Label htmlFor="line_billing_activity">Billing Activity</Label>
                  <select
                    id="line_billing_activity"
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={lineItemForm.billing_activity_id || ''}
                    onChange={async e => {
                      const activityId = parseInt(e.target.value);
                      const activity = billingActivities.find(a => a.id === activityId);
                      if (activity) {
                        setLineItemForm(prev => ({
                          ...prev,
                          billing_activity_id: activityId,
                          cpt_code: activity.cpt_code || prev.cpt_code,
                          hcpcs_code: activity.hcpcs_code || prev.hcpcs_code,
                          revenue_code: activity.revenue_code || prev.revenue_code,
                          modifier_1: activity.modifier_1 || prev.modifier_1,
                          modifier_2: activity.modifier_2 || prev.modifier_2,
                          charge_amount: Number(activity.rate_per_unit) || prev.charge_amount
                        }));

                        const diagCodes = await fetchActivityDiagnosisCodes(activityId);
                        setLineItemDiagnosisCodes(diagCodes);
                        if (diagCodes.length > 0 && !claimForm.diagnosis_code_1) {
                          const defaultDiag = diagCodes.find(d => d.is_default) || diagCodes[0];
                          if (defaultDiag) {
                            setClaimForm(prev => ({ ...prev, diagnosis_code_1: defaultDiag.icd10_code }));
                          }
                        }
                      } else {
                        setLineItemForm(prev => ({ ...prev, billing_activity_id: undefined }));
                        setLineItemDiagnosisCodes([]);
                      }
                    }}
                  >
                    <option value="">Select activity (optional)</option>
                    {billingActivities.map(activity => (
                      <option key={activity.id} value={activity.id}>
                        {activity.activity_name} {activity.cpt_code ? `(${activity.cpt_code})` : ''} - ${activity.rate_per_unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="line_service_date">Service Date *</Label>
                  <Input
                    id="line_service_date"
                    type="date"
                    value={lineItemForm.service_date}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      service_date: e.target.value
                    }))}
                    max={todayLocalISO()}
                    className={`focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${lineItemForm.service_date && lineItemForm.service_date > todayLocalISO() ? 'border-red-500 bg-red-50' : ''}`}
                    title="Service date cannot be in the future"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="line_place_of_service">Place of Service</Label>
                  <select
                    id="line_place_of_service"
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${programPlaceOfService.length > 0 ? 'bg-blue-50' : ''}`}
                    value={lineItemForm.place_of_service || claimForm.place_of_service}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      place_of_service: e.target.value
                    }))}
                  >
                    {availablePlaceOfServiceCodes.map(pos => (
                      <option key={pos.value} value={pos.value}>{pos.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="line_cpt_code">CPT Code</Label>
                  <Input
                    id="line_cpt_code"
                    value={lineItemForm.cpt_code || ''}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      cpt_code: e.target.value
                    }))}
                    placeholder="e.g., 99490"
                    maxLength={5}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="line_hcpcs_code">HCPCS Code</Label>
                  <Input
                    id="line_hcpcs_code"
                    value={lineItemForm.hcpcs_code || ''}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      hcpcs_code: e.target.value.toUpperCase()
                    }))}
                    placeholder="e.g., T1017"
                    maxLength={5}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="line_modifier_1">Modifier 1</Label>
                  <Input
                    id="line_modifier_1"
                    value={lineItemForm.modifier_1 || ''}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      modifier_1: e.target.value.toUpperCase()
                    }))}
                    placeholder="e.g., GT"
                    maxLength={2}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="line_modifier_2">Modifier 2</Label>
                  <Input
                    id="line_modifier_2"
                    value={lineItemForm.modifier_2 || ''}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      modifier_2: e.target.value.toUpperCase()
                    }))}
                    placeholder="e.g., HE"
                    maxLength={2}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="line_units">Units *</Label>
                  <Input
                    id="line_units"
                    type="number"
                    step="0.25"
                    min="0.25"
                    value={lineItemForm.units}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      units: parseFloat(e.target.value) || 1
                    }))}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="line_charge_amount">Charge Amount *</Label>
                  <Input
                    id="line_charge_amount"
                    type="number"
                    step="0.01"
                    value={lineItemForm.charge_amount}
                    onChange={e => setLineItemForm(prev => ({
                      ...prev,
                      charge_amount: parseFloat(e.target.value) || 0
                    }))}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={addLineItem}
                    className="w-full"
                    variant="outline"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Internal Notes */}
          <div>
            <Label htmlFor="internal_notes">Internal Notes</Label>
            <Textarea
              id="internal_notes"
              value={claimForm.internal_notes || ''}
              onChange={e => setClaimForm(prev => ({
                ...prev,
                internal_notes: e.target.value
              }))}
              placeholder="Internal notes (not sent to payer)"
              rows={3}
              className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onNavigate?.('billing-claims')}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateClaim}
            disabled={loading || claimForm.line_items.length === 0}
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Claim
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
